/**
 * The Fixed Assets module's route table (CLAUDE.md §8.8).
 *
 * `src/router/index.ts` is shared by every module, so this file declares the
 * records and the router spreads `ASSETS_ROUTES` in. Keeping them here means
 * the paths, the permissions and the components that implement them are decided
 * in one place by whoever owns the module.
 *
 * Two orderings are load-bearing, and both are the trick AR and bankrec use:
 * `/assets/register/new` and `/assets/register/capitalize` are declared
 * **before** `/assets/register/:id(\d+)`, so neither word can be parsed as an
 * asset id. The `\d+` constraint already makes that safe; the ordering makes it
 * obvious, and the constraint is easy to drop in a later edit.
 *
 * `ASSETS_PATHS` is the same set as strings, for links inside the module. Links
 * *out* of it — the journal a run posted, the supplier invoice an asset was
 * capitalized from, the ledger behind a control account — are built here and
 * then checked against the router by `linkIfRoutable` before being rendered. GL
 * and AP own those URLs; a guessed link that 404s in the middle of an auditor's
 * evidence chain is worse than plain text.
 */

import type { Router, RouteRecordRaw } from 'vue-router'

import { ASSETS_DEPRECIATION_PREVIEW_ROUTES } from '@/modules/assets/depreciation-preview/routes'
import { ASSETS_DISPOSALS_ROUTES } from '@/modules/assets/disposals/routes'
import { ASSETS_IC_TRANSFER_ROUTES } from '@/modules/assets/ic-transfer/routes'
import { ASSETS_PERMISSIONS } from '@/modules/assets/permissions'
import { ASSETS_REPORT_ROUTES } from '@/modules/assets/reports/routes'

export const ASSETS_ROUTES: RouteRecordRaw[] = [
  {
    path: '/assets/classes',
    name: 'assets-classes',
    component: () => import('@/modules/assets/ClassesListView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.classes',
      permission: ASSETS_PERMISSIONS.classRead,
    },
  },
  {
    path: '/assets/register',
    name: 'assets-register',
    component: () => import('@/modules/assets/RegisterListView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.register',
      permission: ASSETS_PERMISSIONS.assetRead,
    },
  },
  {
    path: '/assets/register/new',
    name: 'assets-acquire',
    component: () => import('@/modules/assets/AcquireView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.acquire',
      permission: ASSETS_PERMISSIONS.assetCreate,
    },
  },
  {
    path: '/assets/register/capitalize',
    name: 'assets-capitalize',
    component: () => import('@/modules/assets/CapitalizeView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.capitalize',
      permission: ASSETS_PERMISSIONS.assetCreate,
    },
  },
  {
    path: '/assets/register/:id(\\d+)',
    name: 'assets-asset',
    component: () => import('@/modules/assets/AssetView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.asset',
      permission: ASSETS_PERMISSIONS.assetRead,
    },
  },
  {
    path: '/assets/depreciation',
    name: 'assets-depreciation',
    component: () => import('@/modules/assets/DepreciationRunsView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.depreciation',
      permission: ASSETS_PERMISSIONS.depreciationRun,
    },
  },
  {
    path: '/assets/reports/movement',
    name: 'assets-movement',
    component: () => import('@/modules/assets/MovementReportView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.movement',
      permission: ASSETS_PERMISSIONS.reportRead,
    },
  },

  /* --------------------------------------------- C3: parallel books (§8) */

  {
    path: '/assets/books',
    name: 'assets-books',
    component: () => import('@/modules/assets/BooksView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.books',
      permission: ASSETS_PERMISSIONS.bookRead,
    },
  },
  {
    // Declared before the numeric asset route would matter — it does not, since
    // this path is longer — but kept beside its siblings so the C3 block reads
    // as one addition.
    path: '/assets/register/:id(\\d+)/componentise',
    name: 'assets-componentise',
    component: () => import('@/modules/assets/ComponentiseView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.componentise',
      permission: ASSETS_PERMISSIONS.componentise,
    },
  },
  {
    path: '/assets/impairments',
    name: 'assets-impairments',
    component: () => import('@/modules/assets/ImpairmentsView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.impairments',
      permission: ASSETS_PERMISSIONS.impairmentAssess,
    },
  },
  {
    path: '/assets/verifications',
    name: 'assets-verifications',
    component: () => import('@/modules/assets/VerificationRunsView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.verifications',
      permission: ASSETS_PERMISSIONS.verificationRead,
    },
  },
  {
    path: '/assets/verifications/:id(\\d+)',
    name: 'assets-verification',
    component: () => import('@/modules/assets/VerificationRunView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.verification',
      permission: ASSETS_PERMISSIONS.verificationRead,
    },
  },
  {
    path: '/assets/reports/book-comparison',
    name: 'assets-book-comparison',
    component: () => import('@/modules/assets/BookComparisonView.vue'),
    meta: {
      titleKey: 'assets.routeTitles.bookComparison',
      permission: ASSETS_PERMISSIONS.reportRead,
    },
  },

  /* ------------------------------------------------ FT13-U20 (FSD 2.2.4) */

  // Disposals, per-legal-entity asset settings, inter-company transfers, the depreciation
  // preview and the two FIN-F04 reports. Each is a per-feature file; none shadows a path above.
  ...ASSETS_DISPOSALS_ROUTES,
  ...ASSETS_IC_TRANSFER_ROUTES,
  ...ASSETS_DEPRECIATION_PREVIEW_ROUTES,
  ...ASSETS_REPORT_ROUTES,
]

/* ------------------------------------------------------------ own screens */

export const ASSETS_PATHS = {
  classes: '/assets/classes',
  register: '/assets/register',
  acquire: '/assets/register/new',
  capitalize: '/assets/register/capitalize',
  asset: (id: number) => `/assets/register/${id}`,
  depreciation: '/assets/depreciation',
  movement: '/assets/reports/movement',
} as const

/** The register, pre-filtered — what a movement report row drills through to. */
export function registerRoute(params: {
  companyId?: number | null
  classId?: number | null
  status?: string
}): string {
  const query = new URLSearchParams()
  if (params.companyId !== null && params.companyId !== undefined) {
    query.set('companyId', String(params.companyId))
  }
  if (params.classId !== null && params.classId !== undefined) {
    query.set('classId', String(params.classId))
  }
  if (params.status !== undefined && params.status !== '') query.set('status', params.status)
  const qs = query.toString()
  return qs === '' ? ASSETS_PATHS.register : `${ASSETS_PATHS.register}?${qs}`
}

/* -------------------------------------------------------- links elsewhere */

/** A posted journal entry — every acquisition, run, revaluation and disposal has one. */
export function journalRoute(entryId: number): string {
  return `/gl/journal-entries/${entryId}`
}

/**
 * The general ledger for one account up to a date — the hop from a movement
 * report's control-account reconciliation to the postings that produced it. The
 * window travels with the link so the page it opens adds up to the figure that
 * was clicked.
 */
export function ledgerRoute(companyId: number, accountId: number, to: string): string {
  const query = new URLSearchParams({
    companyId: String(companyId),
    accountId: String(accountId),
  })
  if (to !== '') query.set('to', to)
  return `/gl/reports/ledger?${query.toString()}`
}

/**
 * The supplier invoice an asset was capitalized from.
 *
 * AP serves every document kind from one screen at `/ap/invoices/{id}` — the
 * kind is a column on `ap_invoices`, not a route — so a link built from an
 * asset's source line never has to know it.
 */
export function apInvoiceRoute(invoiceId: number): string {
  return `/ap/invoices/${invoiceId}`
}

/**
 * The path, but only if this app actually has a screen for it.
 *
 * The router's catch-all matches everything, so "resolves" is not the question —
 * "resolves to something other than the not-found page" is. A path the router
 * would send to the 404 screen comes back as null and the caller renders text.
 */
export function linkIfRoutable(router: Router, path: string | null): string | null {
  if (path === null || path === '') return null
  try {
    const resolved = router.resolve(path)
    if (resolved.matched.length === 0) return null
    if (resolved.matched.every((record) => record.name === 'not-found')) return null
    return path
  } catch {
    // An unparseable path is not a link either.
    return null
  }
}
